import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Form } from "antd";
import { useState } from "react";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import { useGetUserPermissionsQuery } from "app/configApiSlice";

import {
  useApproveBatchesMutation,
  usePushAllBatchToNIBSSMutation,
  usePushSingleBatchToNIBSSMutation,
  useRefreshSingleBatchStatusMutation,
  useRefreshTRXNStatusMutation,
  useUpdateApproverMutation,
} from "../tPlusOneProcessingApiSlice";

import {
  setIsApproveBulkBatchModalOpen,
  setIsApproveSingleBatchModalOpen,
  setIsChangeBulkBatchApproverModalOpen,
  setIsChangeSingleBatchApproverModalOpen,
  setIsPushSingleBatchToNIBSSModalOpen,
  setIsPushToNIBSSModalOpen,
  setIsRefreshSingleBatchStatusOpen,
  setIsRefreshStatusModalOpen,
  setSelectedBatchRows,
} from "../tPlusOneProcessingSlice";

import { openNotificationWithIcon } from "../../../utils";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useSearchSummaryBatchQuery } from "../../Generation/generationApiSlice";

const useTPlusOneProcessing = () => {
  const now = dayjs();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [form] = Form.useForm();
  const [batchId, setBatchId] = useState("");

  const { isPushSingleBatchToNIBSSModalOpen, isRefreshSingleBatchStatusOpen, selectedBatchRows } =
    useAppSelector((state) => state.tPlusOneProcessing);

  /** ------------------------------------
   *  QUERY PARAMS
   * ------------------------------------ */
  const [batchSummaryParams, setBatchSummaryParams] = useQueryParams({
    page: withDefault(NumberParam, 0),
    size: withDefault(NumberParam, 10),
    batchId: withDefault(StringParam, ""),
    crBatchStatus: withDefault(StringParam, "EXTRACTED"),
    drBatchStatus: withDefault(StringParam, "EXTRACTED"),
    statuses: withDefault(
      StringParam,
      "PUSHED,APPROVED_AWAITING_PUSH,AWAITING_APPROVAL,APPROVAL_DECLINED",
    ),
    startExtractionDate: withDefault(StringParam, now.startOf("day").format("YYYY-MM-DD")),
    endExtractionDate: withDefault(StringParam, now.endOf("day").format("YYYY-MM-DD")),
  });

  /** ------------------------------------
   * QUERIES / MUTATIONS
   * ------------------------------------ */
  const summaryBatch = useSearchSummaryBatchQuery(batchSummaryParams as any);
  const userPermission = useGetUserPermissionsQuery();
  const [approveBatches, approveBatchResult] = useApproveBatchesMutation();
  const [uppateApprover, updateApproverResult] = useUpdateApproverMutation();
  const [refreshTRXNStatus, refreshTRXNStatusResult] = useRefreshTRXNStatusMutation();
  const [pushAllBatchToNIBSS, pushAllBatchToNIBSSResult] = usePushAllBatchToNIBSSMutation();
  const [pushSingleBatchToNIBSS, pushSingleBatchToNIBSSResult] =
    usePushSingleBatchToNIBSSMutation();
  const [refreshSingleBatchStatus, refreshSingleBatchStatusResult] =
    useRefreshSingleBatchStatusMutation();

  /** ------------------------------------
   *  HELPERS
   * ------------------------------------ */

  const showErrors = (
    error: {
      status?: number;
      originalStatus?: number;
      data?: { errorMessage?: string; errorMessageList?: string[] };
    },
    keepModalOpen = false,
  ) => {
    if (!keepModalOpen) {
      // modal is already closed by caller
    }

    if (error.originalStatus && error.originalStatus >= 500) {
      openNotificationWithIcon(String(error.status), error.data?.errorMessage || "", "error");
      return;
    }

    if (error.status === 400) {
      const list = error.data?.errorMessageList;
      if (list?.length) {
        list.forEach((msg: string) =>
          openNotificationWithIcon(error.data?.errorMessage || "", msg, "warning"),
        );
      } else {
        openNotificationWithIcon(error.data?.errorMessage || "", "", "warning");
      }
    }
  };

  const onSelectChange = (keys: React.Key[]) => dispatch(setSelectedBatchRows(keys as string[]));

  const hasSelected = selectedBatchRows.length > 0;

  /** ------------------------------------
   * TABLE HANDLERS
   * ------------------------------------ */
  const handleTableChange = (pagination: { current?: number; pageSize?: number }) => {
    setBatchSummaryParams({
      page: (pagination.current ?? 1) - 1,
      size: pagination.pageSize,
    });
  };

  const handleFilterChange = (
    name: string,
    value: {
      value?: string | number;
    },
  ) => {
    setBatchSummaryParams({
      [name]: value?.value,
      page: 0,
    });
  };

  /** ------------------------------------
   * ACTION HANDLERS
   * ------------------------------------ */

  const handlePushTRXNToNIBSS = () => {
    pushAllBatchToNIBSS()
      .unwrap()
      .then((payload) => {
        dispatch(setIsPushToNIBSSModalOpen(false));
        openNotificationWithIcon("Successfully sent to NIBSS", payload.response, "success");
      })
      .catch((error) => {
        dispatch(setIsPushToNIBSSModalOpen(true));
        showErrors(error, true);
      });
  };

  const handleRefreshTRXNStatusFunc = () => {
    refreshTRXNStatus()
      .unwrap()
      .then((payload) => {
        dispatch(setIsRefreshStatusModalOpen(false));
        openNotificationWithIcon(
          "Successfully refreshed transaction status",
          payload.response,
          "success",
        );
      })
      .catch((error) => {
        dispatch(setIsRefreshStatusModalOpen(true));
        showErrors(error, true);
      });
  };

  const handleApproveSingleBatch = (batchId: string, params: Record<string, string>) => {
    approveBatches({ batchIds: [batchId], ...params })
      .unwrap()
      .then((payload) => {
        dispatch(setIsApproveSingleBatchModalOpen(false));
        openNotificationWithIcon("", payload.response, "success");
      })
      .catch((error) => {
        dispatch(setIsApproveSingleBatchModalOpen(false));
        showErrors(error);
      });
  };

  const handleUpdateSingleBatchApprover = (batchId: string, params: Record<string, string>) => {
    uppateApprover({ batchIds: [batchId], ...params })
      .unwrap()
      .then((payload) => {
        dispatch(setIsChangeSingleBatchApproverModalOpen(false));
        openNotificationWithIcon("", payload.response, "success");
      })
      .catch((error) => {
        dispatch(setIsChangeSingleBatchApproverModalOpen(false));
        showErrors(error);
      });
  };

  const handleApproveBulkBatch = (batchIds: string[], params: Record<string, string>) => {
    approveBatches({ batchIds, ...params })
      .unwrap()
      .then((payload) => {
        dispatch(setSelectedBatchRows([]));
        dispatch(setIsApproveBulkBatchModalOpen(false));
        openNotificationWithIcon("", payload.response, "success");
      })
      .catch((error) => {
        dispatch(setIsApproveBulkBatchModalOpen(false));
        showErrors(error);
      });
  };

  const handleUpdateBulkBatchApprover = (batchIds: string[], params: Record<string, string>) => {
    approveBatches({ batchIds, ...params })
      .unwrap()
      .then((payload) => {
        dispatch(setSelectedBatchRows([]));
        dispatch(setIsChangeBulkBatchApproverModalOpen(false));
        openNotificationWithIcon("", payload.response, "success");
      })
      .catch((error) => {
        dispatch(setIsChangeBulkBatchApproverModalOpen(false));
        showErrors(error);
      });
  };

  const handlePushSingleBatchFunc = (batchId: string) => {
    pushSingleBatchToNIBSS({ batchId })
      .unwrap()
      .then((payload) => {
        dispatch(setIsPushSingleBatchToNIBSSModalOpen(false));
        openNotificationWithIcon(
          `Successfully pushed batch ${batchId} to NIBSS`,
          payload.response,
          "success",
        );
      })
      .catch((error) => {
        dispatch(setIsPushSingleBatchToNIBSSModalOpen(false));
        showErrors(error);
      });
  };

  const handleRefreshSingleBatchFunc = (batchId: string) => {
    refreshSingleBatchStatus({ batchId })
      .unwrap()
      .then((payload) => {
        dispatch(setIsRefreshSingleBatchStatusOpen(false));
        openNotificationWithIcon(
          `Successfully refreshed batch ${batchId}`,
          payload.response,
          "success",
        );
      })
      .catch((error) => {
        dispatch(setIsRefreshSingleBatchStatusOpen(false));
        showErrors(error);
      });
  };

  /** ------------------------------------
   *  EXPOSED API
   * ------------------------------------ */
  return {
    // basic
    now,
    form,
    batchId,
    navigate,
    setBatchId,

    // table
    selectedBatchRows,
    hasSelected,
    onSelectChange,
    handleTableChange,
    handleFilterChange,

    // queries
    summaryBatch,
    userPermission,
    batchSummaryParams,

    // mutation results
    approveBatchResult,
    updateApproverResult,
    refreshTRXNStatusResult,
    pushAllBatchToNIBSSResult,
    pushSingleBatchToNIBSSResult,
    refreshSingleBatchStatusResult,

    // modal flags
    isRefreshSingleBatchStatusOpen,
    isPushSingleBatchToNIBSSModalOpen,

    // mutation handlers
    handlePushTRXNToNIBSS,
    handleApproveBulkBatch,
    handleApproveSingleBatch,
    handleUpdateBulkBatchApprover,
    handleUpdateSingleBatchApprover,
    handleRefreshTRXNStatus: handleRefreshTRXNStatusFunc,
    handlePushSingleBatchToNIBSS: handlePushSingleBatchFunc,
    handleRefreshSingleBatchStatus: handleRefreshSingleBatchFunc,
  };
};

export default useTPlusOneProcessing;
